import { useEffect, useState } from 'react'
import { Icon } from './Icon'

export type ModuleSettingsMode = 'view' | 'columns' | 'print' | 'print-preview'

export type ModulePreferences = {
  pageSize: string
  density: string
  autoRefresh: boolean
  orientation: string
  paperSize: string
  includeSummary: boolean
}

type Props = {
  mode: ModuleSettingsMode
  moduleTitle: string
  featureTitle: string
  columns: string[]
  visibleColumns: boolean[]
  preferences: ModulePreferences
  rows: string[][]
  onClose: () => void
  onSaveColumns: (columns: boolean[]) => void
  onSavePreferences: (preferences: ModulePreferences) => void
}

const TITLES: Record<ModuleSettingsMode, string> = {
  view: 'View Settings',
  columns: 'Column Settings',
  print: 'Print Settings',
  'print-preview': 'Print Preview',
}

export function ModuleSettingsModal({
  mode,
  moduleTitle,
  featureTitle,
  columns,
  visibleColumns,
  preferences,
  rows,
  onClose,
  onSaveColumns,
  onSavePreferences,
}: Props) {
  const [draftColumns, setDraftColumns] = useState([...visibleColumns])
  const [draftPreferences, setDraftPreferences] = useState({ ...preferences })
  const title = TITLES[mode]

  useEffect(() => {
    setDraftColumns([...visibleColumns])
    setDraftPreferences({ ...preferences })
  }, [mode, visibleColumns, preferences])

  const save = () => {
    if (mode === 'columns') onSaveColumns(draftColumns)
    else onSavePreferences(draftPreferences)
    onClose()
  }

  const toggleColumn = (index: number) => {
    setDraftColumns((current) => current.map((value, itemIndex) => itemIndex === index ? !value : value))
  }

  const visibleIndexes = columns.map((_, index) => index).filter((index) => visibleColumns[index])

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className={`modal-panel module-settings-modal${mode === 'print-preview' ? ' module-settings-modal--preview' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} — ${moduleTitle}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-panel__head">
          <div className="module-modal-title">
            <span className="eyebrow">{moduleTitle} · {featureTitle}</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="icon-btn" title="Close" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="modal-panel__body">
          {mode === 'view' ? (
            <div className="module-settings-grid">
              <label>
                <span>Rows per page</span>
                <select
                  value={draftPreferences.pageSize}
                  onChange={(event) => setDraftPreferences((current) => ({ ...current, pageSize: (event.target as HTMLSelectElement).value }))}
                >
                  <option>10</option>
                  <option>25</option>
                  <option>50</option>
                  <option>100</option>
                </select>
              </label>
              <label>
                <span>Table density</span>
                <select
                  value={draftPreferences.density}
                  onChange={(event) => setDraftPreferences((current) => ({ ...current, density: (event.target as HTMLSelectElement).value }))}
                >
                  <option>Comfortable</option>
                  <option>Compact</option>
                </select>
              </label>
              <label className="module-setting-check">
                <input
                  type="checkbox"
                  checked={draftPreferences.autoRefresh}
                  onChange={(event) => setDraftPreferences((current) => ({ ...current, autoRefresh: (event.target as HTMLInputElement).checked }))}
                />
                <span>Auto-refresh this view</span>
              </label>
            </div>
          ) : null}

          {mode === 'columns' ? (
            <div className="module-column-settings">
              <div className="module-column-settings__actions">
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDraftColumns(columns.map(() => true))}>Select all</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDraftColumns(columns.map((_, index) => index === 0))}>Required only</button>
              </div>
              <div className="module-column-settings__list">
                {columns.map((column, index) => (
                  <label key={column}>
                    <input
                      type="checkbox"
                      checked={draftColumns[index]}
                      disabled={index === 0}
                      onChange={() => toggleColumn(index)}
                    />
                    <span>{column}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {mode === 'print' ? (
            <div className="module-settings-grid">
              <label>
                <span>Orientation</span>
                <select
                  value={draftPreferences.orientation}
                  onChange={(event) => setDraftPreferences((current) => ({ ...current, orientation: (event.target as HTMLSelectElement).value }))}
                >
                  <option>Landscape</option>
                  <option>Portrait</option>
                </select>
              </label>
              <label>
                <span>Paper size</span>
                <select
                  value={draftPreferences.paperSize}
                  onChange={(event) => setDraftPreferences((current) => ({ ...current, paperSize: (event.target as HTMLSelectElement).value }))}
                >
                  <option>Letter</option>
                  <option>A4</option>
                  <option>Legal</option>
                </select>
              </label>
              <label className="module-setting-check">
                <input
                  type="checkbox"
                  checked={draftPreferences.includeSummary}
                  onChange={(event) => setDraftPreferences((current) => ({ ...current, includeSummary: (event.target as HTMLInputElement).checked }))}
                />
                <span>Include summary and filters</span>
              </label>
            </div>
          ) : null}

          {mode === 'print-preview' ? (
            <article className={`module-print-sheet module-print-sheet--${preferences.orientation.toLowerCase()}`}>
              <header>
                <div>
                  <strong>{moduleTitle}</strong>
                  <span>{featureTitle}</span>
                </div>
                <small>{preferences.paperSize} · {preferences.orientation}</small>
              </header>
              <table>
                <thead>
                  <tr>{visibleIndexes.map((index) => <th key={columns[index]}>{columns[index]}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={`${row[0]}-${rowIndex}`}>
                      {visibleIndexes.map((index) => <td key={`${index}-${row[index]}`}>{row[index]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </article>
          ) : null}
        </div>

        <div className="modal-panel__foot">
          <span className="module-modal-spacer" />
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            {mode === 'print-preview' ? 'Close' : 'Cancel'}
          </button>
          {mode !== 'print-preview' ? (
            <button type="button" className="btn btn--primary btn--sm" onClick={save}>
              <Icon name="check" /> Save settings
            </button>
          ) : (
            <button type="button" className="btn btn--primary btn--sm" onClick={() => window.print()}>
              <Icon name="reports" /> Print
            </button>
          )}
        </div>
      </section>
    </div>
  )
}
